import { useEffect, useRef, useState } from "react";
import type { CareProposeCommand, CareRequestView, WorldBodyCareView } from "@/shared/contracts";
import { newIdempotencyKey } from "@/shared/api/idempotency";
import { toApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { useServices } from "@/shared/services/registry";
import { useOptionalCurrentHousehold } from "@/shared/session/householdContext";
import "./world-body-care.css";

type Pending = { kind: "propose" | "cancel"; key: string };
const fallback = new Map<string, Pending>();
function pending(scope: string): Pending | null {
  try {
    const raw = sessionStorage.getItem(scope);
    if (raw) {
      const value = JSON.parse(raw) as Partial<Pending>;
      if ((value.kind === "propose" || value.kind === "cancel") && typeof value.key === "string") return value as Pending;
      // An unreadable original record is not permission to submit again.
      return { kind: "propose", key: "unconfirmed" };
    }
  } catch { return fallback.get(scope) ?? { kind: "propose", key: "unconfirmed" }; }
  return fallback.get(scope) ?? null;
}
function remember(scope: string, value: Pending | null) {
  if (value) fallback.set(scope, value); else fallback.delete(scope);
  try {
    if (value) sessionStorage.setItem(scope, JSON.stringify(value)); else sessionStorage.removeItem(scope);
  } catch { /* Retain the same-scope in-memory guard; never turn storage failure into success. */ }
}
const PARTS: Record<string, string> = {
  head: "头部", tail: "尾巴", ear_l: "左耳", ear_r: "右耳", limb_fl: "左前腿", limb_fr: "右前腿",
  limb_hl: "左后腿", limb_hr: "右后腿", wing_l: "左翅膀", wing_r: "右翅膀", leg_l: "左腿", leg_r: "右腿",
};
const partName = (id: string) => Object.hasOwn(PARTS, id) ? PARTS[id] : "这个部位";
/** 一处调整说成要做的事（I 10-15：只写「尾巴」看不出要干什么）：没有了 → 补回来，只有一部分 → 补完整；别的变化照旧只说部位 */
export function careChangeName(change: { part_id: string; from_state: string; to_state: string }) {
  const part = partName(change.part_id);
  if (change.to_state === "present" && change.from_state === "absent") return `把${part}补回来`;
  if (change.to_state === "present" && change.from_state === "partial") return `把${part}补完整`;
  return part;
}
function requestLine(request: CareRequestView) {
  if (request.state === "expired") return "先前的护理提议已经过期。";
  if (request.state === "superseded") return "情况有了变化，先前的护理提议已失效。";
  if (request.state === "cancelled") return "先前的护理提议已取消。";
  if (request.state === "unknown") return "先前的护理提议还没有明确结果。";
  return "家里有一份未结的护理提议。";
}

/** Only S2 proposal/cancel. Account/PET change synchronously unmounts old state. */
export function WorldBodyCare({ petId }: { petId: string }) {
  const userId = useOptionalCurrentHousehold()?.userId;
  if (env.dataMode !== "live") return <section className="ps-world-care"><h3>护理提议</h3><p>演示模式不能提交真实护理提议。</p></section>;
  if (!userId) return null;
  return <CarePanel key={JSON.stringify([userId, petId])} userId={userId} petId={petId} />;
}

function CarePanel({ userId, petId }: { userId: string; petId: string }) {
  const { pets } = useServices();
  const scope = "care-s2-pending:" + JSON.stringify([userId, petId]);
  const [view, setView] = useState<WorldBodyCareView | null>(null);
  const [chosen, setChosen] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(true);
  const [readFailed, setReadFailed] = useState(false);
  const [unconfirmed, setUnconfirmed] = useState<Pending | null>(() => pending(scope));
  const [note, setNote] = useState("");
  const alive = useRef(true);
  const sending = useRef(false);
  const readAbort = useRef<AbortController | null>(null);

  async function refresh() {
    if (!alive.current) return;
    readAbort.current?.abort();
    const controller = new AbortController();readAbort.current = controller;
    setReading(true);
    try {
      const current = await pets.worldBodyCare(petId, controller.signal);
      if (!alive.current || controller.signal.aborted) return;
      if (current.pet_id !== petId) throw new Error("care identity mismatch");
      setView(current);setReadFailed(false);setChosen([]);
      // GET is current domain evidence, not an original command receipt.
      // Keep any unknown write locked; do not manufacture an acknowledgement.
    } catch {
      if (alive.current && !controller.signal.aborted) setReadFailed(true);
    } finally {
      if (alive.current && !controller.signal.aborted) setReading(false);
    }
  }
  useEffect(() => {
    alive.current = true;void refresh();
    return () => { alive.current = false;readAbort.current?.abort(); };
    // Services are fixed for this account/PET-mounted panel. No stale cache is shared.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [petId, userId, pets]);

  async function command(kind: Pending["kind"]) {
    if (sending.current || reading || readFailed || !view || pending(scope)) return;
    const request = view.open_request;
    const changes = view.eligible_changes.filter(change => chosen.includes(change.part_id));
    if (kind === "propose" && (!view.can_propose || !changes.length || !Number.isInteger(view.world_revision))) return;
    if (kind === "cancel" && !request?.can_cancel) return;
    sending.current = true;setBusy(true);setNote("");
    const original: Pending = { kind, key: newIdempotencyKey("care-" + kind) };
    remember(scope, original);
    try {
      let receipt: CareRequestView;
      if (kind === "propose") {
        const body: CareProposeCommand = { changes: changes.map(change => ({ part_id: change.part_id, to_state: change.to_state })), expected_world_revision: view.world_revision! };
        receipt = await pets.proposeWorldBodyCare(petId, body, original.key);
      } else {
        receipt = await pets.cancelWorldBodyCare(petId, request!.request_id, { expected_request_version: request!.version }, original.key);
      }
      remember(scope, null);
      if (alive.current) {
        setUnconfirmed(null);setNote(receipt.state === "cancelled" ? "提议已经取消。" : "护理提议记下了。");
        await refresh();
      }
    } catch (error) {
      const err = toApiError(error);
      const definite = err.kind === "http" && err.status !== null && err.status >= 400 && err.status < 500
        && err.status !== 408 && !["in_progress", "unknown"].includes(String(err.details?.original_state))
        && err.details?.reason !== "idempotency_in_progress" && err.details?.recovery !== "result_unknown";
      if (definite) remember(scope, null);
      if (alive.current) {
        setUnconfirmed(definite ? null : original);
        setNote(definite ? "情况刚有变化，先看看最新的。" : "");
        await refresh();
      }
    } finally {
      sending.current = false;if (alive.current) setBusy(false);
    }
  }

  const locked = busy || reading || readFailed || unconfirmed !== null;
  return <section className="ps-world-care" aria-label="护理提议">
    <h3>护理提议</h3>
    <p>这里只记录想调整星球上样子的提议，暂时不会改变 TA 的身体或形象。</p>
    {unconfirmed ? <p role="status">{unconfirmed.kind === "cancel" ? "取消还没有明确结果。" : "提议还没有明确结果。"}请查看最新情况。</p> : null}
    {reading ? <p role="status">正在看看最新情况…</p> : null}
    {readFailed ? <p role="alert">这会儿没能读到最新情况，先再看看。</p> : null}
    {note ? <p role="status">{note}</p> : null}
    {view && !readFailed ? <>
      {view.open_request ? <div className="ps-world-care__request">
        <p>{requestLine(view.open_request)}</p>
        <ul>{view.open_request.changes.map(change => <li key={change.part_id}>{careChangeName(change)}</li>)}</ul>
        {view.open_request.can_cancel ? <button type="button" disabled={locked} onClick={() => void command("cancel")}>取消这份提议</button> : <p>这份提议暂时不能由你取消。</p>}
      </div> : null}
      {view.available && view.can_propose && view.eligible_changes.length > 0 && Number.isInteger(view.world_revision) ? <>
        <fieldset disabled={locked}><legend>你想为哪一处提出调整？</legend>
          {view.eligible_changes.map(change => <label key={change.part_id}><input type="checkbox" checked={chosen.includes(change.part_id)} onChange={event => setChosen(prev => event.target.checked ? [...prev, change.part_id] : prev.filter(id => id !== change.part_id))} />{careChangeName(change)}</label>)}
        </fieldset>
        <button type="button" disabled={locked || chosen.length === 0} onClick={() => void command("propose")}>提出护理提议</button>
      </> : !view.open_request ? <p>{view.available ? "目前没有可以提出的护理调整。" : "护理提议暂时还没开放。"}</p> : null}
    </> : null}
    <button type="button" className="ps-world-care__read" disabled={busy || reading} onClick={() => void refresh()}>查看最新情况</button>
  </section>;
}
